import { useState, useEffect, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ConfigContext } from '../shared/ConfigContext';

// Every figure here comes straight from GET /ga/metrics/overview — no sums,
// averages or date-bucketing happen client-side (CHANGES.md decision 4).
const useGADashboardPage = () => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const [searchParams] = useSearchParams();
  const previewState = searchParams.get('previewState');

  const [status, setStatus] = useState('loading');
  const [errorInfo, setErrorInfo] = useState(null);
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchMetrics = useCallback((isRefresh = false) => {
    if (!config?.backendUrl) return;
    if (isRefresh) setRefreshing(true); else setStatus('loading');
    axios.get(`${config.backendUrl}/ga/metrics/overview`)
      .then((res) => {
        setData(res.data);
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error fetching GA overview metrics', error);
        if (error.response?.status === 403 || error.response?.status === 401) {
          setStatus('permission');
        } else {
          setErrorInfo({
            request: `GET /ga/metrics/overview · ${error.response?.status || t('N/A')} · ${new Date().toLocaleTimeString()}`,
          });
          setStatus('error');
        }
      })
      .finally(() => setRefreshing(false));
  }, [config, t]);

  useEffect(() => { fetchMetrics(false); }, [fetchMetrics]);

  const handleRefresh = () => fetchMetrics(true);

  const isPreviewEmptyKind = previewState === 'empty';
  const effectiveStatus = previewState || status;
  const effectiveData = isPreviewEmptyKind ? {
    companies: { value: 0, delta: 0, sparkline: null },
    totalUsers: { value: 0, delta: 0, sparkline: null },
    ordersLast30Days: { value: 0, delta: null, sparkline: [] },
    inactiveCompanies: { value: 0 },
    signupsWeekly: [],
    companiesByStatus: [],
    topCompanies: [],
    attention: [],
  } : data;

  return {
    t, effectiveStatus, data: effectiveData, errorInfo,
    refreshing, handleRefresh, fetchMetrics,
  };
};

export default useGADashboardPage;
